import React, { useState } from "react";
import { useMatter } from "../../../contexts/MatterContext";
import { Field, Fieldset, Label } from "../../../design/fieldset";
import { Heading, Subheading } from "../../../design/heading";
import { Input } from "../../../design/input";
import { Button } from "../../../design/button";
import { RelationshipDetails } from "../../../models/RelationshipDetails";
import {
  relationshipStatusArray,
  RelationshipStatus,
} from "../../../models/RelationshipStatus";
import { Divider } from "../../../design/divider";
import { Select } from "../../../design/select";
import { stat } from "fs";

interface RelationshipDetailsSectionProps {}

const RelationshipDetailsSection: React.FC<
  RelationshipDetailsSectionProps
> = ({}) => {
  const { updateRelationshipDetails, currentMatter } = useMatter();
  const [relationshipDetails, setRelationshipDetails] = useState<
    RelationshipDetails | undefined
  >(currentMatter?.relationshipDetails);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (field: keyof RelationshipDetails, value: any) => {
    setRelationshipDetails({
      ...relationshipDetails,
      [field]: value,
    });
  };

  const handleSubmit = async () => {
    if (!relationshipDetails) return;

    setIsSaving(true);
    try {
      await updateRelationshipDetails(currentMatter?._id!, relationshipDetails);
    } catch (error) {
      console.error("Error updating relationship details:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const status = relationshipDetails?.relationshipStatus;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <Heading>Relationship Details</Heading>
        <Button
          onClick={handleSubmit}
          disabled={
            isSaving ||
            JSON.stringify(relationshipDetails) ===
              JSON.stringify(currentMatter?.relationshipDetails)
          }
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>

      {/* Section 1: Relationship Status */}
      <Fieldset>
        <Subheading>Relationship Status</Subheading>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field>
            <Label>Status</Label>
            <Select
              value={relationshipDetails?.relationshipStatus ?? ""}
              onChange={(e) =>
                handleChange(
                  "relationshipStatus",
                  e.target.value as RelationshipStatus,
                )
              }
            >
              <option value="">Select status</option>
              {Object.keys(relationshipStatusArray).map((statusKey) => (
                <option key={statusKey} value={statusKey}>
                  {
                    relationshipStatusArray[
                      statusKey as keyof typeof relationshipStatusArray
                    ]
                  }
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Fieldset>

      <Divider />

      {/* Section 2: Marriage Details */}
      {status !== "unmarried" && (
        <>
          <Fieldset>
            <Subheading>Marriage Details</Subheading>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field>
                <Label>Date of Marriage</Label>
                <Input
                  type="date"
                  value={
                    relationshipDetails?.dateOfMarriage
                      ? new Date(relationshipDetails.dateOfMarriage)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    handleChange("dateOfMarriage", e.target.value)
                  }
                />
              </Field>

              <Field>
                <Label>City of Marriage</Label>
                <Input
                  type="text"
                  value={relationshipDetails?.cityOfMarriage ?? ""}
                  onChange={(e) =>
                    handleChange("cityOfMarriage", e.target.value)
                  }
                  placeholder="Enter city"
                />
              </Field>

              <Field>
                <Label>State of Marriage</Label>
                <Input
                  type="text"
                  value={relationshipDetails?.stateOfMarriage ?? ""}
                  onChange={(e) =>
                    handleChange("stateOfMarriage", e.target.value)
                  }
                  placeholder="Enter state"
                />
              </Field>

              <Field>
                <Label>Country of Marriage</Label>
                <Input
                  type="text"
                  value={relationshipDetails?.countryOfMarriage ?? ""}
                  onChange={(e) =>
                    handleChange("countryOfMarriage", e.target.value)
                  }
                  placeholder="Enter country"
                />
              </Field>
            </div>
          </Fieldset>

          <Divider />

          {/* Section 3: Separation/Divorce */}
          {(status === "separated" || status === "divorced") && (
            <>
              <Fieldset>
                <Subheading>Separation & Divorce</Subheading>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Field>
                    <Label>Date of Separation</Label>
                    <Input
                      type="date"
                      value={
                        relationshipDetails?.dateOfSeparation
                          ? new Date(relationshipDetails.dateOfSeparation)
                              .toISOString()
                              .split("T")[0]
                          : ""
                      }
                      onChange={(e) =>
                        handleChange("dateOfSeparation", e.target.value)
                      }
                    />
                  </Field>
                  {status === "divorced" && (
                    <Field>
                      <Label>Date of Divorce</Label>
                      <Input
                        type="date"
                        value={
                          relationshipDetails?.dateOfDivorce
                            ? new Date(relationshipDetails.dateOfDivorce)
                                .toISOString()
                                .split("T")[0]
                            : ""
                        }
                        onChange={(e) =>
                          handleChange("dateOfDivorce", e.target.value)
                        }
                      />
                    </Field>
                  )}
                </div>
              </Fieldset>

              <Divider />
            </>
          )}
        </>
      )}

      {/* Section 4: Prenuptial Agreement */}
      {status && status !== "unmarried" && (
        <Fieldset>
          <Subheading>Prenuptial Agreement</Subheading>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field>
              <Label>Has Prenup?</Label>
              <Select
                value={relationshipDetails?.hasPrenup ? "yes" : "no"}
                onChange={(e) =>
                  handleChange("hasPrenup", e.target.value === "yes")
                }
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </Select>
            </Field>
          </div>
        </Fieldset>
      )}
    </div>
  );
};

export default RelationshipDetailsSection;
